import { c as createFocusTrap } from './focus-trap.esm-CwGG3vRJ.js';
import { getFocusableElements } from './utils-get-focusable-elements.js';

/**
 * Drawer Usage:
 * Call the open(), close() or toggle() method to toggle the drawer
 *
 *
 * Drawer Toggle controllers:
 * Add these data attributes to any element to make it control a drawer:
 *
 * aria-controls="drawer-id"    // Required: unique ID of the drawer to control
 * aria-expanded="false"        // Required: 'true' or 'false'
 * data-drawer-action="toggle"  // Optional: 'open', 'close', or 'toggle' (default)
 *
 * Example:
 * <button aria-controls="cart-drawer" aria-expanded="false">Open Cart</button>
 */

class DrawerElement extends HTMLElement {
  static {
    // Let the document listen for clicks on drawer controllers
    document.addEventListener('click', (event) => {
      const controller = event.target.closest('[data-drawer-controller]');
      const drawerId = controller?.getAttribute('aria-controls');
      if (!drawerId) return;

      const { drawerAction } = controller.dataset;
      const drawer = document.getElementById(drawerId);
      if (!drawer) {
        console.error(`Drawer with id '${drawerId}' not found`);
        return;
      }
      drawer.handleAction(drawerAction);
      event.preventDefault();
    });
  }

  connectedCallback() {
    // Set the options
    const { subscribeEventName, subscribeEventAction } = this.dataset;
    this.options = { subscribeEventName, subscribeEventAction };

    // Get the modal body
    this.modalBody = this.querySelector('[data-drawer-body]');
    if (!this.modalBody) return;

    // Listen for key:escape events
    this.unsubscribeEscapeKey = subscribe('key:escape', () => this.close());

    // Create the focus trap
    this.trap = createFocusTrap(this.modalBody, {
      // Allow clicks outside the modal to close the drawer
      allowOutsideClick: true,
      initialFocus: false,
    });

    // Activate the trap if the drawer is rendered open
    if (this.ariaHidden === 'false') {
      this.trap.activate();
    }

    // Subscribe to the event
    this.subscribeEvents();
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey();
  }

  /**
   * Handle drawer actions
   * @param {string} action - The action to perform ('open', 'close', or 'toggle')
   */
  handleAction(action) {
    switch (action) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      default:
        this.toggle();
        break;
    }
  }

  /**
   * Handle drawer controls
   */
  updateControllers() {
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((element) => {
      element.setAttribute('aria-expanded', this.ariaHidden === 'false' ? 'true' : 'false');
    });
  }

  /**
   * Open the drawer
   */
  open() {
    // Activate the focus trap after the transition ends
    const handleTransition = (event) => {
      if (event.target !== this.modalBody) return;
      this.trap.activate();
      this.modalBody.removeEventListener('transitionend', handleTransition);
    };
    // Only listen for transitionend when opening the drawer
    this.modalBody.addEventListener('transitionend', handleTransition);

    //Add class to body when drawer is open to prevent scroll
    document.body.classList.add('overflow-hidden');

    this.ariaHidden = false;
    this.toggleBackdrop(false);
    this.updateControllers();

    this.publishEvent('drawer:opened');
  }

  /**
   * Close the drawer
   */
  close() {
    this.trap.deactivate();

    //Remove class to body when drawer is closed to allow scroll
    document.body.classList.remove('overflow-hidden');
    setTimeout(() => {
      this.ariaHidden = true;
      this.toggleBackdrop(true);
      this.updateControllers();
      this.publishEvent('drawer:closed');
    }, 100);
  }

  /**
   * Toggle the drawer
   */
  toggle() {
    this.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Toggle the backdrop
   * @param {boolean} hide - Whether to hide the backdrop
   */
  toggleBackdrop(hide) {
    this.backdrop = this.backdrop || this.querySelector('[data-backdrop]');
    this.backdrop.ariaHidden = hide;
  }

  /**
   * Update the trap container content elements, always add the header and footer.
   * @param {HTMLElement} element - The new element to trap focus on
   */
  updateTrapContainerElements(element) {
    this.drawerHeader = this.drawerHeader || this.querySelector('.drawer__header');
    this.drawerFooter = this.drawerFooter || this.querySelector('.drawer__footer');
    const elementsArray = [this.drawerHeader, element, this.drawerFooter].filter(Boolean); // Remove null/undefined elements

    this.trap.updateContainerElements(elementsArray);

    // Get focusable elements from each container and combine them
    const focusableElements = elementsArray.flatMap((el) => getFocusableElements(el));

    // Focus the first focusable element if it exists
    focusableElements?.[0]?.focus();
  }

  /**
   * Publish the event
   */
  publishEvent(eventName) {
    publish(`${eventName}:${this.id}`, {
      target: this.id,
    });
  }

  /**
   * Subscribe to the event
   */
  subscribeEvents() {
    if (!this.options.subscribeEventName || !this.options.subscribeEventAction) return;

    subscribe(this.options.subscribeEventName, () => {
      this.handleAction(this.options.subscribeEventAction);
    });
  }
}

customElements.define('drawer-element', DrawerElement);

export { DrawerElement };
